import React from 'react';
import Base from '../Base';
import Title from '../../components/common/Title';
import { useTranslation } from 'react-i18next';
import { apiRequest, fetchUserInfo } from '../../utils';
import { useEffect, useState } from 'react';
import Card from '../../components/elements/Card';
import Button from '../../components/elements/Button';
import Icon from '../../components/common/Icon';
import notificationConfig from '../../config/notificationConfig.json';
import Back from '../../components/common/Back';


const Page = () => {
  const { t, i18n } = useTranslation(["common", "notifications"]);

  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // "Message your team" composer (players on a team only).
  const [showCompose, setShowCompose] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);

  const reload = () => fetchUserInfo('/notifications', setUserInfo, setLoading, setError);
  useEffect(() => {
    fetchUserInfo('/notifications', setUserInfo, setLoading, setError);
  }, []);

  const sendTeamMessage = async () => {
    const text = messageText.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await apiRequest('POST', '/team-message', { message: text });
      setShowCompose(false);
      setMessageText('');
      setToast(t('teamMessageSent'));
      reload();
    } catch (e) {
      setToast(t(e.message || 'somethingWentWrong'));
    } finally {
      setSending(false);
      setTimeout(() => setToast(null), 2600);
    }
  };

  // Helper to replace placeholders in redirectUrl
  const replaceUrlPlaceholders = (url, data) => {
    return url.replace(/{{(\w+)}}/g, (_, key) => data[key] || key);
  };

  const getNotificationDetails = (messageName, rawData = {}) => {
    // 1. Fetch the translatable text from i18n
    const messageTemplate = `notifications:${messageName}`;
    
    // 2. Fetch metadata (icon, redirectUrl, dynamic key mappings)
    const meta = notificationConfig[messageName] || {};
    const icon = meta.icon || 'exclamation-circle';
    let redirectUrl = meta.redirectUrl || '#';

    // 3. Convert `dynamic_key_1` and `dynamic_key_2` to meaningful names
    const dynamicData = {};
    if (meta.dynamicKey1 && rawData.dynamic_key_1) {
      dynamicData[meta.dynamicKey1] = rawData.dynamic_key_1;
    }
    if (meta.dynamicKey2 && rawData.dynamic_key_2) {
      dynamicData[meta.dynamicKey2] = rawData.dynamic_key_2;
    }

    if (rawData.redirect_key_1) {
      dynamicData["redirect_key_1"] = rawData.redirect_key_1;
    }

    // Localize reason for bonus-points notifications: backend sends
    // "readiness_<criterion>"; show the translated criterion label instead.
    if (messageName === 'bonus_points_awarded' && typeof dynamicData.reason === 'string' && dynamicData.reason.startsWith('readiness_')) {
      const criterionKey = dynamicData.reason.slice('readiness_'.length);
      dynamicData.reason = t(`readinesstext.${criterionKey}`);
    }

    // 4. Replace placeholders in message & URL
    const message = t(messageTemplate, dynamicData);
    redirectUrl = replaceUrlPlaceholders(redirectUrl, dynamicData);

    return { message, icon, redirectUrl };
  };



  return (
    <Base activeNav="notifications">
      <Back to='/home' />
      <Title>{t('notifications')}</Title>

      {userInfo?.can_message_team && (
        <div className="px-2 mb-3">
          <Button theme="primary" mb="0" onClick={() => setShowCompose(true)}>
            <Icon name="comment" className="me-2" theme="light" />{t('messageYourTeam')}
          </Button>
        </div>
      )}

      {userInfo?.notifications?.length > 0 ? (
        <div className="px-2">
          {userInfo.notifications.map((notification) => {
            // e.g. notification.message_name = 'team_challenged'
            //      notification.dynamic_data = { team_name: 'Team ABC', event_name: 'Battle 2025', teamId: 'XYZ123' }
            const details = getNotificationDetails(
              notification.message_name, 
              notification
            );

            return (
              <Button 
                key={notification.id}          // or any unique identifier
                theme={notification.is_read ? "light" : "primary"}
                p="3"
                to={details.redirectUrl}
                mb='2'
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-start">
                    <Icon name={details.icon} className="me-3" theme={notification.is_read ? "primary" : "light"} />
                  </div>
                  <div className='flex-grow-1 text-start' style={{ lineHeight: "1.5" }}>
                    {details.message}
                    {notification.body && (
                      <div style={{ whiteSpace: "pre-wrap", fontSize: "0.9rem", marginTop: "0.25rem" }}>
                        {notification.body}
                      </div>
                    )}
                    <div className="text-muted" style={{ fontSize: "0.75rem", marginTop: "0.2rem" }}>
                    {new Date(notification.created_at).toLocaleString('en-GB', { 
                          year: 'numeric', 
                          month: '2-digit', 
                          day: '2-digit', 
                          hour: '2-digit', 
                          minute: '2-digit', 
                          hour12: false 
                      })}

                    </div>
                  </div>
                  <div className="text-end">
                    <Icon name="angle-right" />
                  </div>
                </div>

              </Button>
            );
          })}
        </div>
      ) : (
        <p>{t('noNotificationsFound')}</p>
      )}

      {showCompose && (
        <div
          onClick={() => !sending && setShowCompose(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '1rem' }}
        >
          <div onClick={(e) => e.stopPropagation()}
               style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', maxWidth: 460, width: '100%', boxShadow: '0 10px 40px -10px rgba(0,0,0,0.4)' }}>
            <h5 className="mb-1">{t('messageYourTeam')}</h5>
            <p className="text-muted" style={{ fontSize: '0.85rem' }}>{t('messageYourTeamIntro')}</p>
            <textarea
              className="form-control"
              rows={5}
              autoFocus
              maxLength={2000}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder={t('messageYourTeamPlaceholder')}
            />
            <div className="d-flex gap-2 justify-content-end mt-3">
              <button className="btn btn-outline-secondary" disabled={sending}
                      onClick={() => setShowCompose(false)}>{t('cancel')}</button>
              <button className="btn btn-primary" disabled={sending || !messageText.trim()}
                      onClick={sendTeamMessage}>
                <Icon name="paper-plane" className="me-1" theme="light" />{t('send')}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position: 'fixed', left: '50%', bottom: '5rem', transform: 'translateX(-50%)', background: '#111', color: '#fff', padding: '0.6rem 1rem', borderRadius: 10, zIndex: 1100, fontSize: '0.9rem', maxWidth: '90%' }}>
          {toast}
        </div>
      )}
    </Base>
  );
};

export default Page;